import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { OFFER_CONTRACT_TYPES, OFFER_TYPES } from "app/constants";
import BookingSearch from "app/pages/Booking/Search/BookingSearch";
import { parseQueries } from "app/utils/routerUtils";
import { ficheProduitPropTypes } from "app/utils/propTypes";
import { bindActionCreators } from "redux";
import { fetchFicheProduit } from "app/pages/Listing/productActionCreators";
import { isAbsoluteUrl } from "app/utils/urlDataExtractor";
import get from "lodash/get";
import { clearBooking, updateBooking } from "app/pages/Booking/bookingActionCreators";
import { FormattedMessage } from "react-intl";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { sendTagOnSearchPageViewed } from "app/utils/analytics";
import { getFallbackRedirectUrl } from "app/reducers/partnerSelector";

const BookingSearchContainer = ({
	ficheProduit,
	fetchFicheProduit,
	isRental,
	updateBooking,
	clearBooking,
	fallbackRedirectUrl,
}) => {
	const [displaySearchEngine, setDisplaySearchEngine] = useState(false);
	const [alertMessage, setAlertMessage] = useState();
	const history = useHistory();
	const location = useLocation();
	const { state: locationState, search } = location;
	const { productUri: productUriFromPath } = useParams(); // fiche produit /:productUri
	const ficheProduitUri = ficheProduit.uri; // uri de la fiche produit récupérer du back ou du session storage

	useEffect(() => {
		const query = parseQueries(search);
		const productUriFromQuery = query.productUri; // /booking/search /booking/search?productUri=my-product

		const hasProductChanged =
			(productUriFromPath && productUriFromPath !== ficheProduitUri) ||
			(productUriFromQuery && productUriFromQuery !== ficheProduitUri);

		// Si le produit a changé, il faut réinitialiser ses choix dans le moteur de recherche sinon
		// il voit les prix d'un précédent produit
		if (hasProductChanged) {
			clearBooking();
		}

		const productUri = productUriFromQuery || ficheProduitUri;

		if (productUri) {
			fetchFicheProduit({ productUri }).then(res => {
				if (res.status === 200) {
					const contractType = get(res.data, "offers[0].pricing.contractType");

					// Dans le cas d'un rental, l'offre AO doit toujours être sélectionné par défaut sinon
					// les durées ne s'affichent pas.
					// On fait un updateBooking ici à cause du FETCH_PRODUCTS_SUCCESS qui clear les selections du user
					// TODO envisager de mettre cette logique dans le booking reducer
					if (contractType === OFFER_CONTRACT_TYPES.RENTAL) {
						updateBooking({
							offer: {
								type: OFFER_TYPES.ACCOMODATION_ONLY,
								contractType: OFFER_CONTRACT_TYPES.RENTAL,
							},
						});
					}

					setDisplaySearchEngine(true);
				} else {
					history.push({ pathname: "/not-found" });
				}
			});
		} else if (isAbsoluteUrl(fallbackRedirectUrl)) {
			window.location = fallbackRedirectUrl;
		} else {
			history.push({ pathname: fallbackRedirectUrl });
		}
	}, [search, productUriFromPath, ficheProduitUri]);

	useEffect(() => {
		const redirectAlertMessageId = locationState && locationState.alertMessageId;
		const alertMessage = redirectAlertMessageId && (
			<FormattedMessage id={`${redirectAlertMessageId}`} />
		);

		setAlertMessage(alertMessage);

		sendTagOnSearchPageViewed();

		// Pour ne pas reafficher le message d'erreur quand le user refait un back de la quote vers le booking search
		// https://spectrum.chat/react/general/how-to-reset-the-this-props-location-state~71f95709-c158-42f1-b489-16cb68daf10b
		history.replace({
			pathname: "/booking/search",
			search: `${search}`,
			state: { alertMessageId: undefined },
		});
	}, [location.pathname]);

	return (
		<BookingSearch
			isRental={isRental}
			alertMessage={alertMessage}
			displaySearchEngine={displaySearchEngine}
		/>
	);
};

BookingSearchContainer.propTypes = {
	ficheProduit: ficheProduitPropTypes,
	isRental: PropTypes.bool,
	fetchFicheProduit: PropTypes.func,
	updateBooking: PropTypes.func,
	clearBooking: PropTypes.func,
	fallbackRedirectUrl: PropTypes.string,
};

const mapStateToProps = state => {
	return {
		// on utilise booking.offer.contractType pour savoir si c'est du rental plutot que ficheProduit.contractType
		// car sinon le panneau Logement est plié si on reload le moteur de rechercher avec une durée sélectionnée
		isRental: get(state.booking, "offer.contractType") === OFFER_CONTRACT_TYPES.RENTAL,
		ficheProduit: state.ficheProduit,
		fallbackRedirectUrl: getFallbackRedirectUrl(state),
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			fetchFicheProduit,
			updateBooking,
			clearBooking,
		},
		dispatch
	);
};

BookingSearchContainer.defaultProps = {
	ficheProduit: {},
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(BookingSearchContainer);
